<script setup>
import { computed, ref, watch, onUnmounted } from 'vue'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { useConfigStore } from '@/stores/ConfigStore'
import { useBillingStore } from '@/stores/BillingStore'
import { useRouter } from 'vue-router'
import { formatNumberWithCommas, getFromLocalStorageByUserId, setToLocalStorageByUserId } from '@/helpers/general'
import { useUserStore } from '@/stores/UserStore'
import { useGlobalStore } from '@/stores/GlobalStore'
import { MONTHS } from '@/constants'

const props = defineProps({
  bar: { type: Boolean, default: false },
})

const configStore = useConfigStore()
const billingStore = useBillingStore()
const userStore = useUserStore()
const globalStore = useGlobalStore()
const router = useRouter()

const open = ref(false)

watch(open, (val) => {
  if (props.bar) globalStore.creditsMenuOpen = val
})
onUnmounted(() => {
  if (props.bar) globalStore.creditsMenuOpen = false
})
const dismissedWarning = ref(!!getFromLocalStorageByUserId('aiCreditsBannerDismissed', userStore.user?.id))

const config = computed(() => configStore.config)
const subscription = computed(() => config.value.subscription)

const availableScans = computed(() => config.value.availableScans ?? 0)

// Plan credits
const planScans = computed(() => billingStore.currentPlan?.scans ?? 0)
const scansUsed = computed(() => subscription.value?.scans_this_month ?? 0)
const scansRemaining = computed(() => Math.max(0, planScans.value - scansUsed.value))
const planProgress = computed(() => planScans.value > 0 ? Math.min(100, (scansRemaining.value / planScans.value) * 100) : 0)
const planDepleted = computed(() => scansUsed.value >= planScans.value)

// Extra/add-on credits
const addOnPurchased = computed(() => subscription.value?.add_on_credits_purchased ?? 0)
const addOnUsed = computed(() => subscription.value?.add_on_credits_used ?? 0)
const addOnRemaining = computed(() => Math.max(0, addOnPurchased.value - addOnUsed.value))

// 3 credit states — derived from subscription data (always present in initial config)
const totalAvailable = computed(() => scansRemaining.value + addOnRemaining.value)
const totalAllocated = computed(() => planScans.value + addOnPurchased.value)
const isRed = computed(() => planScans.value > 0 && scansRemaining.value <= 0 && addOnRemaining.value <= 0)
const isYellow = computed(() => !isRed.value && totalAllocated.value > 0 && totalAvailable.value / totalAllocated.value <= 0.15)
const isLowCredits = computed(() => isRed.value || isYellow.value) // any warning state

// Renewal date. `expiration_date` is the day credits actually renew (the day
// AFTER the last day of the billing cycle) — show it as-is. Previously this
// subtracted a day, so it read "Renews <last cycle day>" and confused users
// into thinking credits renewed that day rather than the next (CDP-2419).
const renewalText = computed(() => {
  const expDate = subscription.value?.expiration_date
  if (!expDate || subscription.value?.complimentary || subscription.value?.will_cancel) return null
  const d = new Date(expDate)
  return `Renews ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`
})

const handleBuyMore = () => {
  billingStore.buyScansDialog = { ...billingStore.buyScansDialog, open: true }
  open.value = false
}

const handleUpgrade = () => {
  billingStore.upgradeWindow = true
  //router.push('/settings/plans')
  open.value = false
}

const handleDismissWarning = () => {
  dismissedWarning.value = true
  setToLocalStorageByUserId(true, 'aiCreditsBannerDismissed', userStore.user?.id)
  open.value = false
}
</script>

<template>
  <Popover v-if="config.hasActiveSubscription && !configStore.loadingConfig" class="relative" :class="{ 'w-full': bar }">
    <!-- Bar mode: full-width mobile trigger -->
    <PopoverButton
      v-if="bar"
      class="w-full focus:outline-none"
      @click="open = !open"
    >
      <div
        class="mx-2 mb-1 rounded-xl px-3 py-1 border flex items-center gap-x-2"
        :class="isRed ? 'bg-red-100 border-red-200 text-red-600' : isYellow ? 'bg-yellow-100 border-yellow-200 text-yellow-500' : 'bg-tint-1 border-transparent text-white'"
      >
        <Icon
          icon="heroicons:sparkles-16-solid"
          class="text-lg flex-none"
        />
        <div class="flex-1 flex flex-col gap-y-1 min-w-0">
          <div class="flex items-center justify-between gap-x-1">
            <span
              class="text-xs font-semibold truncate"
              :class="isRed ? 'text-red-600' : isYellow ? 'text-yellow-500' : 'text-white'"
            >{{ formatNumberWithCommas(availableScans) }} AI credits left</span>
            <Icon
              icon="heroicons:chevron-down-16-solid"
              class="text-sm pointer-events-none transition duration-200 flex-none"
              :class="[isRed ? 'text-red-600' : isYellow ? 'text-yellow-500' : 'text-white', { 'rotate-180': open }]"
            />
          </div>
          <div v-if="planScans > 0" class="w-full rounded-full h-1.5" :class="isLowCredits ? 'bg-white/60' : 'bg-blue-800/50'">
            <div
              class="h-1.5 rounded-full transition-all"
              :class="isRed ? 'bg-red-600' : isYellow ? 'bg-yellow-400' : 'bg-white'"
              :style="{ width: planProgress + '%' }"
            />
          </div>
        </div>
      </div>
    </PopoverButton>

    <!-- Inline mode: compact header button (desktop) -->
    <PopoverButton
      v-else
      class="flex items-center gap-x-1.5 rounded-lg px-2.5 text-sm font-semibold focus:outline-none transition border border-blue-800 h-[34px]"
      :class="isRed ? 'bg-red-100 text-red-600 border-red-200' : isYellow ? 'bg-yellow-100 text-yellow-500' : 'bg-blue-600 text-white hover:bg-blue-700 '"
      @click="open = !open"
    >
      <Icon icon="heroicons:sparkles-20-solid" class="text-base flex-none" />
      <div class="flex flex-col justify-center gap-y-0.5 flex-1">
        <span class="leading-none">{{ formatNumberWithCommas(availableScans) }} AI Credits</span>
        <div v-if="planScans > 0" class="w-full rounded-full h-[3px]" :class="isLowCredits ? 'bg-white/60' : 'bg-blue-800/50'">
          <div
            class="h-[3px] rounded-full transition-all"
            :class="isRed ? 'bg-red-600' : isYellow ? 'bg-yellow-400' : 'bg-white'"
            :style="{ width: planProgress + '%' }"
          />
        </div>
      </div>
      <Icon
        icon="heroicons:chevron-down-16-solid"
        class="text-base pointer-events-none transition duration-200 flex-none"
        :class="{ 'rotate-180': open }"
      />
    </PopoverButton>

    <!-- Dropdown panel -->
    <PopoverPanel
      v-if="open"
      v-click-outside="() => open = false"
      static
      class="bg-white shadow-xl border border-gray-100 focus:outline-none overflow-hidden"
      :class="bar
        ? 'absolute z-[101] top-full left-2 right-2 w-auto rounded-xl'
        : 'absolute z-[101] top-full right-0 mt-1 w-80 rounded-xl'"
    >
      <!-- Warning banner -->
      <div
        v-if="isLowCredits && !dismissedWarning"
        class="flex gap-2 px-4 py-3 border-b items-center"
        :class="isRed ? 'bg-red-50 border-red-100' : 'bg-yellow-100'"
      >
        <Icon
          :icon="!isRed ? 'heroicons:exclamation-triangle-16-solid' : 'heroicons:exclamation-circle-16-solid'"
          class="text-2xl mt-0.5 flex-none"
          :class="isRed ? 'text-red-500' : 'text-yellow-400'"
        />
        <div class="flex items-start gap-2">
          <div class="flex-1 text-sm" :class="isRed ? 'text-red-600' : 'text-yellow-500'">
            <span class="font-semibold">{{ isRed ? 'Service Paused' : 'Low Credit Balance' }}</span>
            <p class="mt-0.5 text-xs font-normal" :class="isRed ? 'text-red-900' : 'text-yellow-900'">{{ isRed ? `Recharge or upgrade your plan to continue scanning cards.` : `Recharge or upgrade your plan to avoid any service interruptions.` }}</p>
          </div>
          <button
            class="flex-none"
            :class="isRed ? 'text-red-600 hover:text-red-800' : 'text-yellow-500 hover:text-yellow-700'"
            @click="handleDismissWarning"
          >
            <Icon icon="heroicons:x-mark-16-solid" class="text-base" />
          </button>
        </div>
      </div>

      <div class="p-4 flex flex-col gap-y-3">
        <!-- Total credits left -->
        <div class="-mx-4 pb-4 px-4 border-b border-gray-100">
          <p class="text-sm font-semibold text-gray-900">
            {{ formatNumberWithCommas(availableScans) }} AI Credits left
          </p>
        </div>

        <!-- Included plan credits -->
        <div v-if="planScans > 0" class="flex flex-col gap-y-1.5">
          <div class="flex items-center justify-between text-xs">
            <div
              class="flex items-center gap-x-1.5 text-gray-500 font-semibold"
            >
              <span class="text-gray-900">Included</span>
              <button v-if="!configStore.isComplimentary && (configStore.getConfigField('subscription')?.plan_id !== billingStore.topPlan?.id || !configStore.getConfigField('hasActiveSubscription'))" class="text-tint-1 font-semibold hover:underline" @click="handleUpgrade">Upgrade Plan</button>
            </div>
            <span class="text-gray-600 tabular-nums ml-auto">
              <template v-if="isLowCredits">
                <span class="text-gray-900 font-semibold">{{ formatNumberWithCommas(scansUsed) }}</span><span class="text-gray-600 font-normal">{{ ` / ${formatNumberWithCommas(planScans)} used` }}</span>
              </template>
              <template v-else>
                <span class="text-gray-900 font-semibold">{{ formatNumberWithCommas(scansRemaining) }}</span><span class="text-gray-600 font-normal">{{ ` / ${formatNumberWithCommas(planScans)} remaining` }}</span>
              </template>
            </span>
          </div>
          <div class="w-full bg-gray-100 rounded-full h-1.5">
            <div
              class="h-1.5 rounded-full transition-all"
              :class="isRed ? 'bg-red-500' : isYellow ? 'bg-amber-500' : 'bg-tint-1'"
              :style="{ width: planProgress + '%' }"
            />
          </div>
        </div>

        <!-- Renewal date -->
        <p v-if="renewalText" class="text-xs text-gray-400">{{ renewalText }}</p>

        <!-- Extra/add-on credits — just the remaining count -->
        <div v-if="addOnPurchased > 0" class="flex items-center justify-between text-xs">
          <span class="text-gray-900 font-semibold">Extra credits</span>
          <span class="text-gray-900 font-semibold tabular-nums">{{ formatNumberWithCommas(addOnRemaining) }}</span>
        </div>
      </div>

      <!-- Buy More Credits button -->
      <div class="px-4 pb-4">
        <button
          class="w-full primary-gradient-button justify-center py-2 text-sm font-semibold rounded-lg"
          @click="handleBuyMore"
        >
          Buy More Credits
        </button>
      </div>
    </PopoverPanel>
  </Popover>
</template>
